import { fetchWithChatFeedback } from './chatFeedbackRequired';
/** Official DD-2579 form seam; coordination decisions live in marketResearchDd2579Functions. */
import { apiCall } from './apiClient';
import { mapGpcDd2579, type GpcDd2579 } from './gpcFunctions';
import { USER_FACING_ERROR_MESSAGES } from '../utils/userFacingError';

export type MarketResearchDd2579Form = GpcDd2579;

export type FieldProvenanceKind =
  | 'saved'
  | 'user_confirmed'
  | 'deterministic'
  | 'ai_suggestion'
  | 'conflict'
  | 'manual_required';

export type FieldProvenanceStatus = 'current' | 'review_required' | 'unresolved';

export interface FieldProvenance {
  kind: FieldProvenanceKind;
  label: string;
  source_stage: number | null;
  source_path: string;
  status: FieldProvenanceStatus;
}

export interface PrefillConflictValue {
  product_key: string;
  product_name: string;
  value: string;
}

export interface PrefillConflict {
  field: string;
  message: string;
  values: PrefillConflictValue[];
}

export type MarketResearchDd2579AiStatus = 'generated' | 'cached' | 'degraded' | 'not_needed';
export type MarketResearchDd2579WarningKind = 'stale' | 'review_required' | 'ai_unavailable';
export type MarketResearchDd2579NarrativeField =
  | 'co_remarks'
  | 'other_than_full_open_reason'
  | 'prior_award_details';

export interface MarketResearchDd2579PrefillResponse {
  dd2579: MarketResearchDd2579Form;
  field_provenance: Partial<Record<keyof MarketResearchDd2579Form, FieldProvenance>>;
  conflicts: PrefillConflict[];
  unresolved_fields: string[];
  from_cache: boolean;
  prefill_inputs_hash: string;
  ai_status: MarketResearchDd2579AiStatus;
  saved_form_present: boolean;
  prefill_complete: boolean;
  export_ready: boolean;
  warning: string | null;
  warning_kind?: MarketResearchDd2579WarningKind | null;
  refreshed_suggestions: Partial<Record<MarketResearchDd2579NarrativeField, string>>;
  /** Echoes the merge policy used for this response; saved_form_present remains a persistence fact. */
  used_saved_work: boolean;
  /** True only when the displayed response is exactly the persisted output.dd2579 form. */
  displayed_form_saved: boolean;
  /** False for degraded/failed explicit refills whose result must not replace browser state. */
  replacement_applied: boolean;
}

export interface RestoredMarketResearchDd2579State {
  form: MarketResearchDd2579Form;
  /** False for the durable empty first-entry sentinel; mapped default fields are not a draft. */
  hasSavedOrCachedDraft?: boolean;
  savedFormPresent: boolean;
  prefillComplete: boolean;
  exportReady: boolean;
  fieldProvenance: MarketResearchDd2579PrefillResponse['field_provenance'];
  conflicts: PrefillConflict[];
  unresolvedFields: string[];
  prefillInputsHash: string;
  aiStatus: MarketResearchDd2579AiStatus;
  warning: string | null;
  warningKind?: MarketResearchDd2579WarningKind | null;
  /** Explicit null tri-state choices in an unsaved legacy cache are user draft values, not blank prefill slots. */
  preservedDraftFields: Array<keyof MarketResearchDd2579Form>;
}

const provenanceKinds = new Set<FieldProvenanceKind>([
  'saved', 'user_confirmed', 'deterministic', 'ai_suggestion', 'conflict', 'manual_required',
]);
const provenanceStatuses = new Set<FieldProvenanceStatus>(['current', 'review_required', 'unresolved']);
const aiStatuses = new Set<MarketResearchDd2579AiStatus>(['generated', 'cached', 'degraded', 'not_needed']);
const warningKinds = new Set<MarketResearchDd2579WarningKind>(['stale', 'review_required', 'ai_unavailable']);
const mapWarningKind = (value: unknown): MarketResearchDd2579WarningKind | null =>
  warningKinds.has(value as MarketResearchDd2579WarningKind) ? value as MarketResearchDd2579WarningKind : null;
const narrativeFields = new Set<MarketResearchDd2579NarrativeField>([
  'co_remarks', 'other_than_full_open_reason', 'prior_award_details',
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const valueIsPresent = (value: unknown): boolean =>
  typeof value === 'string' ? value.trim().length > 0 : value !== null && value !== undefined;

const authoritativeNullableBooleanFields = new Set<keyof MarketResearchDd2579Form>([
  'full_and_open',
  'market_research_done',
  'synopsis_required',
  'progress_payments',
  'consolidated',
  'bundled',
  'subcontracting_plan_required',
  'is_new_requirement',
  'prior_consolidated',
  'prior_bundled',
  'sbp_concur',
  'sba_pcr_concur',
  'co_review_concur',
  'vendor_is_small',
]);

/** Null is an authoritative saved value only for modeled tri-state booleans. */
export const isMarketResearchDd2579SavedValue = (
  field: keyof MarketResearchDd2579Form,
  value: unknown,
): boolean => valueIsPresent(value)
  || (value === null && authoritativeNullableBooleanFields.has(field));

const fallbackProvenance = (
  form: MarketResearchDd2579Form,
  kind: 'saved' | 'manual_required' = 'manual_required',
): MarketResearchDd2579PrefillResponse['field_provenance'] =>
  Object.fromEntries(Object.entries(form).map(([field, value]) => {
    const saved = kind === 'saved'
      && isMarketResearchDd2579SavedValue(field as keyof MarketResearchDd2579Form, value);
    return [field, {
      kind: saved ? 'saved' : 'manual_required',
      label: saved ? 'Saved DD 2579' : 'Manual entry required',
      source_stage: saved ? 13 : null,
      source_path: saved ? `output.dd2579.${field}` : '',
      status: saved ? 'current' : (valueIsPresent(value) ? 'review_required' : 'unresolved'),
    } satisfies FieldProvenance];
  })) as MarketResearchDd2579PrefillResponse['field_provenance'];

const mapProvenance = (
  value: unknown,
  form: MarketResearchDd2579Form,
  fallbackKind: 'saved' | 'manual_required' = 'manual_required',
): MarketResearchDd2579PrefillResponse['field_provenance'] => {
  const result = fallbackProvenance(form, fallbackKind);
  if (!isRecord(value)) return result;
  for (const key of Object.keys(form) as Array<keyof MarketResearchDd2579Form>) {
    const item = value[String(key)];
    if (!isRecord(item)) continue;
    const kind = item.kind;
    const status = item.status;
    if (!provenanceKinds.has(kind as FieldProvenanceKind)
      || !provenanceStatuses.has(status as FieldProvenanceStatus)
      || typeof item.label !== 'string'
      || (item.source_stage !== null && typeof item.source_stage !== 'number')
      || typeof item.source_path !== 'string') continue;
    // A blank/default value still needs user input; never imply that a null or
    // empty placeholder is authoritative just because a malformed producer
    // labeled the slot as saved.
    if (kind === 'saved' && !isMarketResearchDd2579SavedValue(key, form[key])) continue;
    result[key] = {
      kind: kind as FieldProvenanceKind,
      label: item.label,
      source_stage: item.source_stage as number | null,
      source_path: item.source_path,
      status: status as FieldProvenanceStatus,
    };
  }
  return result;
};

const mapConflicts = (value: unknown): PrefillConflict[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item): PrefillConflict[] => {
    if (!isRecord(item) || typeof item.field !== 'string' || typeof item.message !== 'string'
      || !Array.isArray(item.values)) return [];
    const values = item.values.flatMap((entry): PrefillConflictValue[] => (
      isRecord(entry)
      && typeof entry.product_key === 'string'
      && typeof entry.product_name === 'string'
      && typeof entry.value === 'string'
        ? [{ product_key: entry.product_key, product_name: entry.product_name, value: entry.value }]
        : []
    ));
    return [{ field: item.field, message: item.message, values }];
  });
};

const mapRefreshedSuggestions = (
  value: unknown,
): Partial<Record<MarketResearchDd2579NarrativeField, string>> => {
  if (!isRecord(value)) return {};
  const result: Partial<Record<MarketResearchDd2579NarrativeField, string>> = {};
  for (const [key, suggestion] of Object.entries(value)) {
    if (narrativeFields.has(key as MarketResearchDd2579NarrativeField) && typeof suggestion === 'string') {
      result[key as MarketResearchDd2579NarrativeField] = suggestion;
    }
  }
  return result;
};

const mapPrefillResponse = (data: unknown): MarketResearchDd2579PrefillResponse => {
  const structured = isRecord(data) && isRecord(data.dd2579);
  const envelope = structured ? data : {};
  const dd2579 = mapMarketResearchDd2579Form(structured ? envelope.dd2579 : data);
  const legacyCombinedReadiness = structured && envelope.prefill_export_ready === true;
  return {
    dd2579,
    field_provenance: mapProvenance(structured ? envelope.field_provenance : null, dd2579),
    conflicts: mapConflicts(structured ? envelope.conflicts : null),
    unresolved_fields: structured && Array.isArray(envelope.unresolved_fields)
      ? envelope.unresolved_fields.filter((field): field is string => typeof field === 'string')
      : [],
    from_cache: structured && envelope.from_cache === true,
    prefill_inputs_hash: structured && typeof envelope.prefill_inputs_hash === 'string'
      ? envelope.prefill_inputs_hash : '',
    ai_status: structured && aiStatuses.has(envelope.ai_status as MarketResearchDd2579AiStatus)
      ? envelope.ai_status as MarketResearchDd2579AiStatus : 'not_needed',
    saved_form_present: structured && envelope.saved_form_present === true,
    prefill_complete: structured && (envelope.prefill_complete === true || legacyCombinedReadiness),
    export_ready: structured && (envelope.export_ready === true || legacyCombinedReadiness),
    warning: structured && typeof envelope.warning === 'string' ? envelope.warning : null,
    warning_kind: mapWarningKind(envelope.warning_kind),
    refreshed_suggestions: mapRefreshedSuggestions(structured ? envelope.refreshed_suggestions : null),
    used_saved_work: !structured || envelope.used_saved_work !== false,
    displayed_form_saved: structured && envelope.displayed_form_saved === true,
    replacement_applied: !structured || envelope.replacement_applied !== false,
  };
};

function errorFrom(response: { error?: string; error_code?: string; technical_detail?: string }) {
  const error = new Error(response.error ?? USER_FACING_ERROR_MESSAGES.maintainers_alerted) as Error & { error_code?: string; technical_detail?: string };
  error.error_code = response.error_code;
  error.technical_detail = response.technical_detail;
  return error;
}

export const mapMarketResearchDd2579Form = (data: unknown): MarketResearchDd2579Form => mapGpcDd2579(data);

export interface MarketResearchDd2579ViewState {
  form: MarketResearchDd2579Form | null;
  export_ready: boolean;
  saved_form_present: boolean;
  reason: string | null;
}

/** Server rechecks saved signatures/readiness without running a prefill or saving. */
export async function getMarketResearchDd2579ViewState(flowId: string): Promise<MarketResearchDd2579ViewState> {
  const apiBaseUrl = process.env.REACT_APP_API_URL || '';
  const response = await fetchWithChatFeedback(`${apiBaseUrl}/api/market-research/dd2579/view-state?flow_id=${encodeURIComponent(flowId)}`, {
    method: 'GET', credentials: 'include',
  });
  const body = await response.json();
  if (!response.ok || !body.success) throw errorFrom(body);
  const data = body.data || {};
  return {
    form: data.form && typeof data.form === 'object' ? mapMarketResearchDd2579Form(data.form) : null,
    export_ready: data.export_ready === true,
    saved_form_present: data.saved_form_present === true,
    reason: typeof data.reason === 'string' ? data.reason : null,
  };
}

export async function generateMarketResearchDd2579Prefill(
  flowId: string,
  forceRecompute = false,
  useSavedWork = true,
): Promise<MarketResearchDd2579PrefillResponse> {
  const response = await apiCall<unknown>('/api/market-research/dd2579/prefill', 'POST', {
    flow_id: flowId,
    force_recompute: forceRecompute,
    use_saved_work: useSavedWork,
  });
  if (!response.success) throw errorFrom(response);
  return mapPrefillResponse(response.data);
}

export async function saveMarketResearchDd2579(flowId: string, dd2579: MarketResearchDd2579Form): Promise<MarketResearchDd2579Form> {
  const response = await apiCall<unknown>('/api/market-research/dd2579/save', 'POST', { flow_id: flowId, dd2579 });
  if (!response.success) throw errorFrom(response);
  return mapMarketResearchDd2579Form(response.data);
}

export function restoreMarketResearchDd2579StateFromStageOutput(output: {
  dd2579?: unknown;
  prefill_cache?: (Record<string, unknown> & { result?: unknown }) | null;
} | null | undefined): RestoredMarketResearchDd2579State | null {
  const savedFormPresent = isRecord(output?.dd2579);
  const possibleCache = output?.prefill_cache;
  const cached = isRecord(possibleCache) ? possibleCache : null;
  // A raw Stage 13 record is intentionally created before the user has saved
  // a form or requested prefill. Its empty output is the durable first-entry
  // sentinel, not a failed restore. Do not extend this fallback to nonempty
  // malformed payloads, which must remain fail-closed for App's retry gate.
  const isEmptyStageOutput = isRecord(output) && Object.keys(output).length === 0;
  const value = savedFormPresent ? output?.dd2579 : cached?.result ?? (isEmptyStageOutput ? {} : undefined);
  if (!isRecord(value)) return null;
  const form = mapMarketResearchDd2579Form(value);
  const rawPreservedDraftFields = cached?.preserved_draft_fields;
  const listedDraftFields = Array.isArray(rawPreservedDraftFields)
    ? rawPreservedDraftFields.filter((field): field is keyof MarketResearchDd2579Form => (
      typeof field === 'string' && field in form
    ))
    : null;
  const isUnversionedLegacyDraft = !cached?.prefill_inputs_hash;
  const preservedDraftFields = savedFormPresent
    ? []
    : listedDraftFields ?? (isUnversionedLegacyDraft
      ? (Object.keys(value) as Array<keyof MarketResearchDd2579Form>)
        .filter((field) => field in form && isMarketResearchDd2579SavedValue(field, value[String(field)]))
      : []);
  const cachedUnresolvedValue = cached?.unresolved_fields;
  const cachedUnresolved = Array.isArray(cachedUnresolvedValue) ? cachedUnresolvedValue : [];
  const legacyCombinedReadiness = cached?.prefill_export_ready === true;
  return {
    form,
    hasSavedOrCachedDraft: savedFormPresent || isRecord(cached?.result),
    savedFormPresent,
    prefillComplete: !savedFormPresent && (cached?.prefill_complete === true || legacyCombinedReadiness),
    exportReady: !savedFormPresent && (cached?.export_ready === true || legacyCombinedReadiness),
    fieldProvenance: mapProvenance(savedFormPresent ? null : cached?.field_provenance, form, savedFormPresent ? 'saved' : 'manual_required'),
    conflicts: savedFormPresent ? [] : mapConflicts(cached?.conflicts),
    unresolvedFields: savedFormPresent
      ? [] : cachedUnresolved.filter((field): field is string => typeof field === 'string'),
    prefillInputsHash: !savedFormPresent && typeof cached?.prefill_inputs_hash === 'string' ? cached.prefill_inputs_hash : '',
    aiStatus: !savedFormPresent && aiStatuses.has(cached?.ai_status as MarketResearchDd2579AiStatus)
      ? cached?.ai_status as MarketResearchDd2579AiStatus : 'not_needed',
    warning: !savedFormPresent && typeof cached?.warning === 'string' ? cached.warning : null,
    warningKind: !savedFormPresent ? mapWarningKind(cached?.warning_kind) : null,
    preservedDraftFields,
  };
}

export function restoreMarketResearchDd2579FromStageOutput(output: {
  dd2579?: unknown;
  prefill_cache?: (Record<string, unknown> & { result?: unknown }) | null;
} | null | undefined): MarketResearchDd2579Form | null {
  return restoreMarketResearchDd2579StateFromStageOutput(output)?.form ?? null;
}

export async function generateMarketResearchDd2579Pdf(flowId: string): Promise<void> {
  const apiBaseUrl = process.env.REACT_APP_API_URL || '';
  const response = await fetchWithChatFeedback(`${apiBaseUrl}/api/market-research/dd2579/pdf`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ flow_id: flowId }) });
  if (!response.ok) throw errorFrom(await response.json().catch(() => ({})));
  const blob = await response.blob();
  const contentType = (response.headers.get('Content-Type') || blob.type)
    .split(';', 1)[0]
    .trim()
    .toLowerCase();
  if (blob.size === 0 || contentType !== 'application/pdf') {
    throw errorFrom({
      error: USER_FACING_ERROR_MESSAGES.wait_and_retry,
      error_code: 'wait_and_retry',
    });
  }
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = response.headers.get('Content-Disposition')?.match(/filename="?([^";]+)"?/i)?.[1] ?? `Market_Research_DD2579_${flowId}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
